'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { FeaturesListType } from '@/types/ui';
import { Globe, Graph, UpRightArray, Location, Wallet } from '@/public/images/icons/icons';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export default function Stats() {
  const t = useTranslations('Stats');
  const cardRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const stats: FeaturesListType[] = [
    {
      title: '270K+',
      description: 'users',
      icon: <Globe width={30} height={30} className="shrink-0 globe-spin" />,
      width: 30,
    },
    {
      title: '€13M+',
      description: 'annual_volume',
      icon: <Graph width={30} height={30} className="shrink-0 bar-animate" />,
      width: 30,
    },
    {
      title: '+30%',
      description: 'yoy_growth',
      icon: <UpRightArray width={30} height={30} className="shrink-0 arrow-nudge" />,
      width: 30,
    },
    {
      title: '260K+',
      description: 'transactions_year',
      icon: <Location width={30} height={30} className="shrink-0 pin-animate" />,
      width: 30,
    },
    {
      title: '2',
      description: 'countries',
      icon: <Wallet width={30} height={30} className="shrink-0 check-animate" />,
      width: 22,
    },
  ];

  return (
    <motion.section
      className="w-full bg-white py-12 px-6 sm:px-10 lg:px-16"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeInUp}
    >
      <div
        ref={cardRef}
        className={`max-w-8xl mx-auto bg-dark rounded-3xl p-8 sm:p-12 shadow-xl${inView ? ' stats-in-view' : ''}`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-0 lg:divide-x lg:divide-slate-800">
          {stats.map((stat: FeaturesListType, index: number) => (
            <div
              key={index}
              className="group flex flex-col items-start lg:px-6 first:lg:pl-0 last:lg:pr-0 border-b border-slate-800/80 pb-6 sm:pb-0 sm:border-b-0"
            >
              <div className="mb-4">
                {stat.icon && (
                  <div className="mb-3">
                    {stat.icon}
                  </div>
                )}
              </div>
              <h3 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
                {index === 4 ? `${stat.title} ${t('countriesLabel')}` : stat.title}
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 font-medium mt-2">
                {t(stat.description)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}